import { useMarketConfig } from "@customer-tools-web/market-extension-resolver";
import * as H from "history";
import * as Cookie from "js-cookie";
import * as psl from "psl";
import { parse, stringify } from "querystring";
import * as React from "react";
import * as intl from "react-intl-universal";
import { useHistory, useLocation } from "react-router";

export type Lang = "en-GB" | "zh-HK" | "zh-CN";
export type originalLangType = "en_GB" | "zh_HK" | "zh_CN";
export type MultiLangText = {
  en_GB: string | null;
  zh_HK?: string | null;
  zh_CN?: string | null;
  ms_MY?: string | null;
};
export type MultiLangTextType = MultiLangText | string | null;
const SUPPORTED_LOCALES: { [key in Lang]: boolean } = {
  "en-GB": true,
  "zh-HK": true,
  "zh-CN": false,
};

const warningHandler = (message: string, detail: string) => {
  if (detail) {
    console.warn(message, detail);
  }
};

const defaultParseMultiLang = (multiLangText?: MultiLangTextType) => {
  if (!multiLangText) {
    return "";
  }
  if (typeof multiLangText === "object") {
    return (
      multiLangText.en_GB || multiLangText.zh_HK || multiLangText.zh_CN || ""
    );
  }
  return multiLangText;
};

export const I18NContext = React.createContext<I18NProps>({
  locale: "en-GB",
  originalLocale: "en_GB",
  t: (key: string) => key,
  h: (key: string) => key,
  parseToLocaleText: defaultParseMultiLang,
  changeLocale: () => undefined,
});

export function useI18N(prefix?: string) {
  const context = React.useContext(I18NContext);
  if (!prefix) {
    return context;
  } else {
    return {
      ...context,
      t: (key: string, args?: object, ingorePrefix = false) => {
        if (prefix && !ingorePrefix) {
          return context.t(`${prefix}.${key}`, args);
        } else {
          return context.t(key, args);
        }
      },
      h: (key: string, args?: object, ingorePrefix = false) => {
        if (prefix && !ingorePrefix) {
          return context.h(`${prefix}.${key}`, args);
        } else {
          return context.h(key, args);
        }
      },
    };
  }
}

export default function <P extends I18NProps = I18NProps>(
  ChildComponent: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof I18NProps>> {
  return (props) => (
    <I18NContext.Consumer>
      {(context) => <ChildComponent {...context} {...(props as P)} />}
    </I18NContext.Consumer>
  );
}

export type ProviderProps = {
  history?: H.History | null;
  resources: { [key in Lang]?: object };
};

type I18NContentProviderProps = {
  state: Lang;
  changeLocale: (locale: string) => void;
};

export interface I18NProps {
  locale: Lang;
  originalLocale: originalLangType;
  changeLocale: (locale: string) => void;
  parseToLocaleText: (multiLangText?: MultiLangTextType) => string;
  t: (key: string, args?: object) => string;
  h: (key: string, args?: object) => string;
}

const URL_LOCALE_KEY = "lang";
const COOKIE_LOCALE_KEY = "ct-lang";

const saveLocaleInCookie = (locale?: string): void => {
  Cookie.set(COOKIE_LOCALE_KEY, locale!, {
    domain: psl.parse(location.host).domain,
    expires: 365,
  });
};

export const determineLocale = (
  fallbackLocale: Lang,
  location?: H.Location
) => {
  let determinedLocale =
    getLocaleFromURL(URL_LOCALE_KEY, (location || window.location).search) ||
    getLocaleFromCookie(COOKIE_LOCALE_KEY);
  if (!SUPPORTED_LOCALES[determinedLocale as Lang]) {
    determinedLocale = fallbackLocale;
  }
  saveLocaleInCookie(determinedLocale);
  return determinedLocale;
};

export const getLocaleFromURL = (
  urlLocaleKey: string,
  locationSearch: string
): string | undefined => {
  if (urlLocaleKey) {
    const query = locationSearch.split("?");
    if (query.length >= 2) {
      const params = parse(query[1]);
      const locale = params && params[urlLocaleKey];
      if (typeof locale === "string") {
        return locale;
      } else if (Array.isArray(locale)) {
        return locale[0];
      }
    }
  }
};

const getLocaleFromCookie = (cookieLocaleKey: string) => {
  return Cookie.get(cookieLocaleKey);
};
function parseToLocaleText(
  originalLocale: originalLangType,
  multiLangText?: MultiLangTextType
) {
  if (!multiLangText) {
    return "";
  }
  if (typeof multiLangText === "object") {
    return (
      multiLangText[originalLocale] || defaultParseMultiLang(multiLangText)
    );
  }
  return multiLangText;
}

function init(locale: Lang, resources: { [key in Lang]?: object }) {
  intl.init({
    currentLocale: locale,
    locales: resources,
    warningHandler,
  });
}

const I18NContextProvider: React.FC<I18NContentProviderProps> = (props) => {
  const { state, changeLocale } = props;

  const value = React.useMemo(() => {
    const originalLocale = state.replace("-", "_") as originalLangType;

    return {
      locale: state,
      originalLocale,
      changeLocale: changeLocale,
      t: (key: string, args?: object) => intl.get(key, args),
      h: (key: string, args?: object) => intl.getHTML(key, args),
      parseToLocaleText: (multiLangText?: MultiLangTextType) =>
        parseToLocaleText(originalLocale, multiLangText),
    };
  }, [state, changeLocale]);

  return (
    <I18NContext.Provider value={value}>{props.children}</I18NContext.Provider>
  );
};

export function I18NProvider(
  props: ProviderProps & { children?: React.ReactNode }
) {
  const history = useHistory();
  const location = useLocation();
  const config = useMarketConfig();
  const currentLocale = determineLocale(
    config.fallbackLocale as Lang,
    location
  );

  init(currentLocale as Lang, props.resources);
  const [state, setState] = React.useState(currentLocale as Lang);

  React.useEffect(() => {
    init(state, props.resources);
    saveLocaleInCookie(currentLocale);
  }, [currentLocale]);

  function changeLocale(locale: string) {
    if (!window) {
      return;
    }
    const { search, pathname, hash } = location;
    const locationSearch =
      search.indexOf("?") === 0 ? search.substring(1) : search;
    const queryParams = parse(locationSearch);
    const nextSearch = stringify({ ...queryParams, [URL_LOCALE_KEY]: locale });
    setState(locale as Lang);
    history.push(`${pathname}?${nextSearch}${hash}`);
  }

  if (state) {
    return (
      <I18NContextProvider state={state} changeLocale={changeLocale}>
        {props.children}
      </I18NContextProvider>
    );
  } else {
    return null;
  }
}
